import React, {ReactElement, useState} from 'react';
// TODO: INSTEAD OF USING MATERIAL UI COMPONENTS DIRECTLY, CREATE GENERIC COMPONENTS IN SYSTEM AND IMPORT IT
import {
  Card,
  CardContent,
  TableCell,
  TableRow,
  makeStyles
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import HelperUtils from '@shared/utils/HelperUtils';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {
  APP,
  // DEFAULT_BLACK_COLOR,
  DEFAULT_WHITE_COLOR,
  FONT_SECONDARY_COLOR
} from 'src/style/variable';

import {pastInvoiceDetails} from '@shared/services/DueInvoiceServices';
import './PreviousStatementCard.scss';
import MinGridTable, {
  ColumnDef
} from '@components/system/MinGridTable/MinGridTable';

const previousStatementCardStyles = makeStyles(theme => ({
  paymentDetailsContentView: {
    overflowY: 'auto',
    maxHeight: '20rem',
    marginTop: '0.5rem'
  },
  paymentsSectionView: {
    flex: 1,
    display: 'flex',
    // paddingTop: '1rem',
    fontSize: '1rem',
    padding: '0.5rem'
  },
  flexView: {
    flex: 1,
    display: 'flex',
    fontSize: '1rem',
    gap: '1rem'
  },
  leftView: {
    display: 'flex',
    // color: DEFAULT_BLACK_COLOR,
    color: '#343434',
    width: '25%'
  },
  rightView: {
    display: 'flex',
    alignItems: 'flexStart',
    justifyContent: 'flexEnd',
    alignContent: 'flexEnd',
    fontWeight: 600,
    color: '#343434',
    // color: DEFAULT_BLACK_COLOR,
    textAlign: 'end'
  },
  middleView: {
    display: 'flex',
    flex: 1,
    color: '#8C8C8C'
    // color: DEFAULT_BLACK_COLOR
  },
  infoText: {
    color: FONT_SECONDARY_COLOR,
    fontSize: '1rem',
    lineHeight: '1rem',
    padding: '1rem'
  },
  applyOddColor: {
    backgroundColor: 'rgb(0,0,0,0.05)'
  },
  applyEvenColor: {
    backgroundColor: DEFAULT_WHITE_COLOR
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  cardContent: {
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem'
    }
  },
  importedDate: {
    paddingBottom: '0.8rem',
    paddingTop: '0.8rem',
    fontWeight: 'bold',
    paddingLeft: '0.5rem'
  },
  headersPadding: {
    paddingTop: 0,
    paddingBottom: 0
  },
  headerText: {
    color: APP.STATUS.DEFAULT,
    fontSize: '1rem'
  },
  divider: {
    marginTop: '0.5rem'
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBlock: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600
  },
  contentSpace: {
    display: 'flex'
  },
  cardHeader: {
    '& .MuiCardContent-root': {
      padding: '0'
    }
  },
  amountWrapper: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  loaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  rightAlignHeader: {
    textAlign: 'right'
  }
}));

interface PreviousStatementsCardProps {
  clinicId: number;
  patientId: number | string;
}

const PreviousStatementsCard = (
  props: PreviousStatementsCardProps
): ReactElement => {
  const {clinicId, patientId} = props;

  const {t} = useTranslation();

  const {enqueueSnackbar} = useSnackbar();

  const classes = previousStatementCardStyles();
  // FIXME: ADD PROPER TYPES
  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const [invoiceLinesData, setInvoiceLinesData]: any = useState({});
  const [
    isLoadingPreviousStatements,
    setIsLoadingPreviousStatements
  ] = useState<boolean>(true);
  const getPatientPreviousStatements = async (): Promise<void> => {
    try {
      const invoiceDetailsResponse = await pastInvoiceDetails(
        groupId,
        clinicId,
        patientId
      );
      if (invoiceDetailsResponse?.data?.prev_statements) {
        setInvoiceLinesData(invoiceDetailsResponse?.data?.prev_statements);
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setIsLoadingPreviousStatements(false);
    }
  };
  React.useEffect(() => {
    if (patientId) {
      getPatientPreviousStatements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const prevStatementColDefs: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.DATE'),
      render(obj) {
        return HelperUtils.formatDisplayDate(obj.service_date);
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.DESCRIPTION'),
      render(obj) {
        return obj.description;
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.AMOUNT'),
      render(obj) {
        if (obj.amount === null) return '';
        return (
          <CurrencyFormatter
            value={obj.amount}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      },
      colValueCellProps: {
        className: 'table-amount-col'
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    }
  ];

  const rows: any = [];
  const importedKeys = Object.keys(invoiceLinesData ?? {});
  importedKeys.forEach((key: any) => {
    const previousImports = invoiceLinesData[key];
    const dateSplits = key?.split('_');
    const month = HelperUtils.formatDisplayDate(
      dateSplits[0] + '/1/' + dateSplits[1],
      'MM/YYYY'
    );
    const importedDate = HelperUtils.formatDisplayDate(dateSplits[2]);

    let locationNameString = '';
    if (previousImports?.location_name) {
      locationNameString = `- ${previousImports?.location_name}`;
    }

    rows.push({
      render: () => (
        <TableRow>
          <TableCell colSpan={3}>
            <div
              style={{
                fontWeight: 'bold'
              }}>
              {t('PATIENT_DETAILS_CARD.PREVIOUS_IMPORT_DATE_FORMAT', {
                month: month,
                imported_date: importedDate
              })}
              {locationNameString}
            </div>
          </TableCell>
        </TableRow>
      )
    });

    previousImports?.lines.map((line: any) => {
      rows.push(line);
    });
  });

  return (
    <div className="prev-statement-card-wrapper">
      <div
        className={[
          classes.sectionRow,
          classes.DrawerInfo,
          classes.contentSpace
        ].join(' ')}>
        <span>{t('DUE_INVOICES.PATIENT_DRAWER.PREVIOUS_STATEMENT')}</span>
      </div>
      <Card className={classes.cardHeader}>
        <CardContent>
          <MinGridTable
            rows={rows}
            colDefs={prevStatementColDefs}
            isLoading={isLoadingPreviousStatements}
            noDataText={t('DUE_INVOICES.NO_PREVIOUS_STATEMENT_DATA')}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PreviousStatementsCard;
